import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar
} from '@mui/material';
import {
  Hotel as HotelIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { getPrePaidBooking, getSpecificHotelBooking, updatePrepaidPaymentStatus } from '../api/adminPanelApi';

const AdminPanelPrepaid = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hotels, setHotels] = useState([]);
  const [totalData, setTotalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Hotel booking states
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [paymentUpdating, setPaymentUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Date filter states for main page
  const [mainSelectedMonth, setMainSelectedMonth] = useState('');
  const [mainStartDate, setMainStartDate] = useState('');
  const [mainEndDate, setMainEndDate] = useState('');
  
  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Initialize date filters for main page
  useEffect(() => {
    const now = new Date();
    const currentMonth = now.toISOString().slice(0, 7); // YYYY-MM format
    const currentDate = now.toISOString().slice(0, 10); // YYYY-MM-DD format
    const firstDayOfMonth = `${currentMonth}-01`;
    
    setMainSelectedMonth(currentMonth);
    setMainStartDate(firstDayOfMonth);
    setMainEndDate(currentDate);
  }, []);

  // Fetch data on component mount
  useEffect(() => {
    const fetchPrepaidData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get current month date range
        const now = new Date();
        const currentMonth = now.toISOString().slice(0, 7); // YYYY-MM format
        const currentDate = now.toISOString().slice(0, 10); // YYYY-MM-DD format
        const firstDayOfMonth = `${currentMonth}-01`;
        
        const response = await getPrePaidBooking(currentMonth, firstDayOfMonth, currentDate);
        
        if (response.success && response.data) {
          const { totalData, perHotelData } = response.data;
          
          // Transform API data to component format
          const transformedHotels = perHotelData.map((hotel, index) => ({
            id: index + 1,
            hotel_id: hotel.hotel_id,
            hotel_name: hotel.hotel_name,
            contact_number: hotel.phone,
            total_bookings: hotel.totalBookings,
            total_revenue: hotel.totalRevenue,
            commission: hotel.totalTTCommission,
            gst: hotel.totalGst,
            total_tabtrips: hotel.tabtripsTotal
          }));
          
          setHotels(transformedHotels);
          setTotalData(totalData);
        } else {
          setError(response.error || 'Failed to fetch prepaid booking data');
        }
      } catch (err) {
        setError('An unexpected error occurred while fetching data');
        console.error('Error fetching prepaid data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPrepaidData();
  }, []);

  // Preselect hotel when navigated from Bookings panel (once)
  const stateHotelId = location.state?.selectedHotelId;
  const stateMonth = location.state?.month;
  const stateStart = location.state?.start;
  const stateEnd = location.state?.end;
  const appliedStateRef = React.useRef(false);
  useEffect(() => {
    if (appliedStateRef.current || !stateHotelId || hotels.length === 0) return;
    const match = hotels.find((h) => String(h.hotel_id) === String(stateHotelId));
    if (match) {
      setSelectedHotel(match);
      appliedStateRef.current = true;
    }
    if (stateMonth && stateStart && stateEnd) {
      setMainSelectedMonth(stateMonth);
      setMainStartDate(stateStart);
      setMainEndDate(stateEnd);
    }
  }, [stateHotelId, hotels, stateMonth, stateStart, stateEnd]);

  // Initialize date filters when a hotel is selected
  useEffect(() => {
    if (selectedHotel) {
      const now = new Date();
      const currentMonth = now.toISOString().slice(0, 7); // YYYY-MM format
      const currentDate = now.toISOString().slice(0, 10); // YYYY-MM-DD format
      const firstDayOfMonth = `${currentMonth}-01`;
      
      setSelectedMonth(currentMonth);
      setStartDate(firstDayOfMonth);
      setEndDate(currentDate);
      setSelectedBookings([]);
      
      // Fetch bookings for the current month
      fetchHotelBookings(selectedHotel.hotel_id, currentMonth, firstDayOfMonth, currentDate);
    }
  }, [selectedHotel]);

  // Utility functions for date management
  const getCurrentMonthDateRange = (monthStr) => {
    const now = new Date();
    const selectedDate = new Date(monthStr + '-01');
    const currentMonth = now.toISOString().slice(0, 7);
    
    if (monthStr === currentMonth) {
      // Current month: start from 1st, end at current date
      return {
        start: `${monthStr}-01`,
        end: now.toISOString().slice(0, 10)
      };
    } else if (monthStr < currentMonth) {
      // Past month: start from 1st, end at last day of that month
      const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();
      return {
        start: `${monthStr}-01`,
        end: `${monthStr}-${lastDay.toString().padStart(2, '0')}`
      };
    } else {
      // Future month: not allowed, return current month
      return getCurrentMonthDateRange(currentMonth);
    }
  };

  const fetchHotelBookings = async (hotelId, month, startDate, endDate) => {
    try {
      setBookingsLoading(true);
      const response = await getSpecificHotelBooking(hotelId, month, startDate, endDate, 'prePaid');
      
      if (response.success && response.data && response.data.bookingData) {
        setBookings(response.data.bookingData);
      } else {
        setBookings([]);
        setSnackbar({
          open: true,
          message: response.error || 'No bookings found for the selected period',
          severity: 'info'
        });
      }
    } catch (error) {
      console.error('Error fetching hotel bookings:', error);
      setBookings([]);
      setSnackbar({
        open: true,
        message: 'Failed to fetch hotel bookings',
        severity: 'error'
      });
    } finally {
      setBookingsLoading(false);
    }
  };

  // Calculate totals from API data or fallback to hotel data
  const totals = totalData ? {
    total_bookings: totalData.totalBookings,
    total_revenue: totalData.totalRevenue,
    commission: totalData.totalTTCommission,
    gst: totalData.totalGst,
    total_tabtrips: totalData.tabtripsTotal
  } : {
    total_bookings: 0,
    total_revenue: 0,
    commission: 0,
    gst: 0,
    total_tabtrips: 0
  };

  // Filter hotels based on search
  const filteredHotels = hotels.filter(hotel =>
    hotel.hotel_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(hotel.hotel_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
    hotel.contact_number.includes(searchTerm)
  );

  const handleHotelClick = (hotel) => {
    setSelectedHotel(hotel);
  };

  const handleBackToHotels = () => {
    setSelectedHotel(null);
    setBookings([]);
    setSelectedBookings([]);
  };

  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    const now = new Date();
    const currentMonth = now.toISOString().slice(0, 7);
    
    // Prevent future month selection
    if (newMonth > currentMonth) {
      setSnackbar({
        open: true,
        message: 'Cannot select future months as no data is available',
        severity: 'warning'
      });
      return;
    }
    
    setSelectedMonth(newMonth);
    const dateRange = getCurrentMonthDateRange(newMonth);
    setStartDate(dateRange.start);
    setEndDate(dateRange.end);
    
    // Fetch bookings for the new month
    if (selectedHotel) {
      fetchHotelBookings(selectedHotel.hotel_id, newMonth, dateRange.start, dateRange.end);
    }
  };

  const handleDateChange = (field, value) => {
    if (field === 'start') {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
  };

  const handleApplyDateFilter = () => {
    if (selectedHotel && selectedMonth && startDate && endDate) {
      fetchHotelBookings(selectedHotel.hotel_id, selectedMonth, startDate, endDate);
    }
  };

  const handleBookingSelection = (bookingId, isChecked) => {
    if (isChecked) {
      setSelectedBookings(prev => [...prev, bookingId]);
    } else {
      setSelectedBookings(prev => prev.filter(id => id !== bookingId));
    }
  };

  const handleSelectAllBookings = (isChecked) => {
    if (isChecked) {
      const unpaidBookings = bookings.filter(booking => booking.paid_to_hotel !== "1").map(booking => booking.booking_id);
      setSelectedBookings(unpaidBookings);
    } else {
      setSelectedBookings([]);
    }
  };

  const handleUpdatePaymentStatus = async () => {
    if (selectedBookings.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select bookings to update payment status',
        severity: 'warning'
      });
      return;
    }

    try {
      setPaymentUpdating(true);
      const response = await updatePrepaidPaymentStatus(selectedBookings, 'Paid');
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: `Successfully updated payment status for ${selectedBookings.length} booking(s)`,
          severity: 'success'
        });
        
        // Refresh bookings
        if (selectedHotel) {
          fetchHotelBookings(selectedHotel.hotel_id, selectedMonth, startDate, endDate);
        }
        setSelectedBookings([]);
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to update payment status',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating payment status:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while updating payment status',
        severity: 'error'
      });
    } finally {
      setPaymentUpdating(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  // Main page date filter handlers
  const handleMainMonthChange = (event) => {
    const newMonth = event.target.value;
    const now = new Date();
    const currentMonth = now.toISOString().slice(0, 7);
    
    // Prevent future month selection
    if (newMonth > currentMonth) {
      setSnackbar({
        open: true,
        message: 'Cannot select future months as no data is available',
        severity: 'warning'
      });
      return;
    }
    
    setMainSelectedMonth(newMonth);
    const dateRange = getCurrentMonthDateRange(newMonth);
    setMainStartDate(dateRange.start);
    setMainEndDate(dateRange.end);
  };

  const handleMainDateChange = (field, value) => {
    if (field === 'start') {
      setMainStartDate(value);
    } else {
      setMainEndDate(value);
    }
  };

  const handleMainApplyDateFilter = () => {
    // Refresh data with new date filters
    fetchPrepaidDataWithFilters();
  };

  const fetchPrepaidDataWithFilters = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getPrePaidBooking(mainSelectedMonth, mainStartDate, mainEndDate);
      
      if (response.success && response.data) {
        const { totalData, perHotelData } = response.data;
        
        // Transform API data to component format
        const transformedHotels = perHotelData.map((hotel, index) => ({
          id: index + 1,
          hotel_id: hotel.hotel_id,
          hotel_name: hotel.hotel_name,
          contact_number: hotel.phone,
          total_bookings: hotel.totalBookings,
          total_revenue: hotel.totalRevenue,
          commission: hotel.totalTTCommission,
          gst: hotel.totalGst,
          total_tabtrips: hotel.tabtripsTotal
        }));
        
        setHotels(transformedHotels);
        setTotalData(totalData);
      } else {
        setError(response.error || 'Failed to fetch prepaid booking data');
      }
    } catch (err) {
      setError('An unexpected error occurred while fetching data');
      console.error('Error fetching prepaid data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} sx={{ color: '#FF7700' }} />
        <Typography variant="h6" color="text.secondary">
          Loading prepaid booking data...
        </Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Error Loading Data
          </Typography>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Please try refreshing the page or contact support if the problem persists.
        </Typography>
      </Box>
    );
  }

  if (selectedHotel) {
    // Hotel Detail View with Bookings
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <IconButton onClick={handleBackToHotels} sx={{ color: '#FF7700' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>
              {selectedHotel.hotel_name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Hotel ID: {selectedHotel.hotel_id} | Phone: {selectedHotel.contact_number}
            </Typography>
          </Box>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              border: '1px solid #90caf9',
              boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <BusinessIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#1565c0', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                      {selectedHotel.total_bookings}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1976d2' }}>Total Bookings</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
              border: '1px solid #ce93d8',
              boxShadow: '0 2px 8px rgba(156, 39, 176, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TrendingUpIcon sx={{ fontSize: 24, color: '#7b1fa2' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#6a1b9a', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                      {formatCurrency(selectedHotel.total_revenue)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#7b1fa2' }}>Total Revenue</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
              border: '1px solid #a5d6a7',
              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ReceiptIcon sx={{ fontSize: 24, color: '#388e3c' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#2e7d32', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                      {formatCurrency(selectedHotel.gst)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#388e3c' }}>GST</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
              border: '1px solid #ffcc80',
              boxShadow: '0 2px 8px rgba(255, 152, 0, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <HotelIcon sx={{ fontSize: 24, color: '#f57c00' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#ef6c00', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                      {formatCurrency(selectedHotel.total_tabtrips)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#f57c00' }}>TabTrips Total</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Date Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057', mb: 3 }}>
            Booking Filters
          </Typography>
          
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Month"
                  onChange={handleMonthChange}
                  startAdornment={<CalendarIcon sx={{ mr: 1, color: '#FF7700' }} />}
                >
                  {Array.from({ length: 12 }, (_, i) => {
                    const date = new Date();
                    date.setMonth(date.getMonth() - i);
                    const monthValue = date.toISOString().slice(0, 7);
                    const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                    return (
                      <MenuItem key={monthValue} value={monthValue}>
                        {monthLabel}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => handleDateChange('start', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().slice(0, 10)
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => handleDateChange('end', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().slice(0, 10)
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyDateFilter}
                sx={{ 
                  backgroundColor: '#FF7700',
                  '&:hover': { backgroundColor: '#e56900' },
                  py: 1.5
                }}
                disabled={bookingsLoading}
              >
                Apply Filter
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Bookings Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057' }}>
              Prepaid Bookings ({bookings.length})
            </Typography>
            
            {selectedBookings.length > 0 && (
              <Button
                variant="contained"
                startIcon={<PaymentIcon />}
                onClick={handleUpdatePaymentStatus}
                disabled={paymentUpdating}
                sx={{
                  backgroundColor: '#43e97b',
                  '&:hover': { backgroundColor: '#38c86a' }
                }}
              >
                {paymentUpdating ? 'Updating...' : `Mark Paid (${selectedBookings.length})`}
              </Button>
            )}
          </Box>
          
          {bookingsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: '#FF7700' }} />
            </Box>
          ) : bookings.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No bookings found for the selected period
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                      <Checkbox
                        checked={selectedBookings.length === bookings.filter(b => b.paid_to_hotel !== "1").length && bookings.filter(b => b.paid_to_hotel !== "1").length > 0}
                        indeterminate={selectedBookings.length > 0 && selectedBookings.length < bookings.filter(b => b.paid_to_hotel !== "1").length}
                        onChange={(e) => handleSelectAllBookings(e.target.checked)}
                        disabled={bookings.filter(b => b.paid_to_hotel !== "1").length === 0}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Booking ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Guest Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Guest Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="right">Discounted Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="right">GST</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="right">Final Paid Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="center">Payment Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.map((booking) => {
                    const isPaid = booking.paid_to_hotel === "1";
                    const isSelected = selectedBookings.includes(booking.booking_id);
                    
                    return (
                      <TableRow 
                        key={booking.booking_id} 
                        hover={!isPaid}
                        sx={{ 
                          opacity: isPaid ? 0.6 : 1,
                          backgroundColor: isPaid ? '#f8f9fa' : 'transparent'
                        }}
                      >
                        <TableCell>
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => handleBookingSelection(booking.booking_id, e.target.checked)}
                            disabled={isPaid}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={booking.booking_id} 
                            size="small" 
                            sx={{ backgroundColor: '#FF7700', color: 'white' }}
                          />
                        </TableCell>
                        <TableCell>{booking.guest_name}</TableCell>
                        <TableCell>{booking.guest_phone}</TableCell>
                        <TableCell align="right">{formatCurrency(parseFloat(booking.discounted_amount))}</TableCell>
                        <TableCell align="right">{formatCurrency(parseFloat(booking.gst_amount))}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={formatCurrency(parseFloat(booking.final_amount_paid))} 
                            size="small" 
                            sx={{ backgroundColor: '#43e97b', color: 'white' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={isPaid ? 'Paid' : 'Pending'} 
                            size="small" 
                            sx={{ 
                              backgroundColor: isPaid ? '#4caf50' : '#ff9800',
                              color: 'white'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
        
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Main Hotels List View
  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton 
            onClick={() => navigate('/')} 
            sx={{ 
              color: '#FF7700',
              '&:hover': { 
                backgroundColor: 'rgba(255, 119, 0, 0.1)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h3" fontWeight={700} sx={{ color: '#2E4057' }}>
            Admin Panel - Prepaid Bookings
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage and monitor all prepaid hotel bookings and revenue
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search hotels by name, ID, or contact number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#FF7700' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              '&:hover fieldset': {
                borderColor: '#FF7700',
              },
            },
          }}
        />
      </Box>

      {/* Date Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057', mb: 3 }}>
          Date Filters
        </Typography>
        
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Month</InputLabel>
              <Select
                value={mainSelectedMonth}
                label="Month"
                onChange={handleMainMonthChange}
                startAdornment={<CalendarIcon sx={{ mr: 1, color: '#FF7700' }} />}
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const date = new Date();
                  date.setMonth(date.getMonth() - i);
                  const monthValue = date.toISOString().slice(0, 7);
                  const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                  return (
                    <MenuItem key={monthValue} value={monthValue}>
                      {monthLabel}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={mainStartDate}
              onChange={(e) => handleMainDateChange('start', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: new Date().toISOString().slice(0, 10)
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={mainEndDate}
              onChange={(e) => handleMainDateChange('end', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: new Date().toISOString().slice(0, 10)
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleMainApplyDateFilter}
              sx={{ 
                backgroundColor: '#FF7700',
                '&:hover': { backgroundColor: '#e56900' },
                py: 1.5
              }}
              disabled={loading}
            >
              Apply Filter
            </Button>
          </Grid>
        </Grid>
      </Paper>

              {/* Statistics Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              border: '1px solid #90caf9',
              boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <BusinessIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#1565c0', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                      {totals.total_bookings}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1976d2' }}>Total Bookings</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
              border: '1px solid #ce93d8',
              boxShadow: '0 2px 8px rgba(156, 39, 176, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TrendingUpIcon sx={{ fontSize: 24, color: '#7b1fa2' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#6a1b9a', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                      {formatCurrency(totals.total_revenue)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#7b1fa2' }}>Total Revenue</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
              border: '1px solid #a5d6a7',
              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ReceiptIcon sx={{ fontSize: 24, color: '#388e3c' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#2e7d32', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                      {formatCurrency(totals.gst)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#388e3c' }}>Total GST</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
              border: '1px solid #ffcc80',
              boxShadow: '0 2px 8px rgba(255, 152, 0, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <HotelIcon sx={{ fontSize: 24, color: '#f57c00' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#ef6c00', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                      {formatCurrency(totals.commission)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#f57c00' }}>Total Commission</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)',
              border: '1px solid #f48fb1',
              boxShadow: '0 2px 8px rgba(233, 30, 99, 0.1)'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TrendingUpIcon sx={{ fontSize: 24, color: '#c2185b' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#ad1457', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                      {formatCurrency(totals.total_tabtrips)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#c2185b' }}>TabTrips Total</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

      {/* Hotels Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057' }}>
            Hotels List
          </Typography>
        </Box>
        
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Hotel ID</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Hotel Name</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Contact Number</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="center">Total Bookings</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="right">Total Revenue</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="right">Commission</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="right">GST</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="right">TabTrips Total</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHotels
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((hotel) => (
                <TableRow 
                  key={hotel.id} 
                  hover 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleHotelClick(hotel)}
                >
                  <TableCell>
                    <Chip 
                      label={hotel.hotel_id} 
                      size="small" 
                      sx={{ backgroundColor: '#FF7700', color: 'white' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HotelIcon sx={{ color: '#FF7700', fontSize: 20 }} />
                      <Typography variant="body2" fontWeight={500}>
                        {hotel.hotel_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ color: '#666', fontSize: 16 }} />
                      <Typography variant="body2">{hotel.contact_number}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={hotel.total_bookings} 
                      size="small" 
                      sx={{ backgroundColor: '#667eea', color: 'white' }}
                    />
                  </TableCell>
                  <TableCell align="right">{formatCurrency(hotel.total_revenue)}</TableCell>
                  <TableCell align="right">{formatCurrency(hotel.commission)}</TableCell>
                  <TableCell align="right">{formatCurrency(hotel.gst)}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={formatCurrency(hotel.total_tabtrips)} 
                      size="small" 
                      sx={{ backgroundColor: '#43e97b', color: 'white' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton 
                      size="small" 
                      sx={{ color: '#FF7700' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHotelClick(hotel);
                      }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredHotels.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default AdminPanelPrepaid; 
